import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { Modal as Miuimodal, Box, Typography, TextField, Button, IconButton, MenuItem, Select, FormControl, InputLabel, Alert, Snackbar, Stack } from '@mui/material';
import { Add, Edit, Search, FilterAlt } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { getValesGasolina, createValeGasolina, updateValeGasolina, getShipmentByReference, getShipments } from '../api';
import GasolinaPDF from '../components/GasolinaPDF'; // Import the GasolinaPDF component
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const Gasolina = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeItem, setActiveItem] = useState('BI-Dashboard');
  const [valesGasolina, setValesGasolina] = useState([]);
  const [filteredVales, setFilteredVales] = useState([]);
  const [newVale, setNewVale] = useState({
    tipo: 'diesel',
    cantidad: '',
    precio: '',
    proveedor: '',
    fecha: new Date().toISOString().split('T')[0]
  });
  const [isModalOpen, setModalOpen] = useState(false);
  const [shipments, setShipments] = useState([]);
  const [warehouseData, setWarehouseData] = useState([]);
  const [searchReference, setSearchReference] = useState('');
  const [searchedShipment, setSearchedShipment] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ show: false, message: '', severity: 'info' });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    folio: '',
    reference: '',
    consignatario: '',
    creationDateRange: [null, null]
  });
  const navigate = useNavigate();
  const location = useLocation();

  const fuelTypes = [
    { value: 'diesel', label: 'Diesel' },
    { value: 'gasolina_magna', label: 'Gasolina Magna' },
    { value: 'gasolina_premium', label: 'Gasolina Premium' }
  ];

  // Show alert function
  const showAlert = (message, severity = 'info') => {
    setAlertInfo({ show: true, message, severity });
  };

  // Close alert function  
  const handleCloseAlert = () => {
    setAlertInfo({ ...alertInfo, show: false });
  };

  useEffect(() => {
    fetchValesGasolina();
    fetchShipments();
    fetchWarehouseData();
    const params = new URLSearchParams(location.search);
    const shipmentReference = params.get('shipment_reference');
    if (shipmentReference) {
      setNewVale({ 
        ...newVale, 
        shipment: shipmentReference 
      });
      handleSearchShipment(shipmentReference);
      setModalOpen(true);
    }
  }, [location.search]);

  // Set filtered vales when vales are fetched
  useEffect(() => {
    setFilteredVales(valesGasolina);
  }, [valesGasolina]);

  const fetchValesGasolina = async () => {
    try {
      const data = await getValesGasolina();
      // Ordenar por fecha de creación (del más reciente al más antiguo)
      const sortedVales = [...data].sort((a, b) => {
        // Primero convertir las fechas a objetos Date para comparación
        const dateA = new Date(a.fecha || 0);
        const dateB = new Date(b.fecha || 0);
        
        // Ordenar de más reciente a más antiguo
        return dateB - dateA;
      });
      setValesGasolina(sortedVales);
      setFilteredVales(sortedVales);
    } catch (error) {
      console.error('Error fetching vales de gasolina:', error);
      showAlert(`Error al cargar vales de gasolina: ${error.message}`, 'error');
    }
  };

  const fetchShipments = async () => {
    try {
      const data = await getShipments();
      setShipments(data);
    } catch (error) {
      console.error('Error fetching shipments:', error);
      showAlert(`Error al cargar embarques: ${error.message}`, 'error');
    }
  };

  const fetchWarehouseData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/bodegas`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setWarehouseData(data);
    } catch (error) {
      console.error('Error fetching warehouse data:', error);
      showAlert(`Error al cargar datos de bodegas: ${error.message}`, 'error');
    }
  };

  const generateUniqueFolio = () => {
    return `VC-${uuidv4().slice(0, 6)}`;
  };

  const handleCreateVale = async () => {
    try {
      if (!newVale.cantidad || !newVale.precio) {
        showAlert('Por favor complete los campos de cantidad y precio', 'warning');
        return;
      }

      if (!newVale.shipment) {
        showAlert('El embarque es requerido', 'warning');
        return;
      }

      setLoading(true);
      const shipment = getShipmentDetails(newVale.shipment);
      const total = parseFloat(newVale.cantidad) * parseFloat(newVale.precio);
      
      const valeData = {
        ...newVale,
        folio: generateUniqueFolio(),
        origen: shipment?.origin || 'No especificado',
        destino: getDestinationCity(shipment) || 'No especificado',
        fecha: newVale.fecha || new Date().toISOString().split('T')[0],
        total: total
      };
      
      const response = await createValeGasolina(valeData);
      setValesGasolina([...valesGasolina, response]);
      setFilteredVales([...valesGasolina, response]);
      showAlert('Vale de gasolina creado correctamente', 'success');
      
      setNewVale({
        tipo: 'diesel',
        cantidad: '',
        precio: '',
        proveedor: '',
        fecha: new Date().toISOString().split('T')[0]
      });
      
      setModalOpen(false);
    } catch (error) {
      console.error('Error creating vale de gasolina:', error);
      showAlert(`Error al crear vale de gasolina: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateVale = async () => {
    try {
      if (!newVale || newVale.id === undefined) {
        showAlert('Error: No se puede actualizar el vale porque el ID no está definido.', 'error');
        return;
      }

      if (!newVale.cantidad || !newVale.precio) {
        showAlert('Por favor complete los campos de cantidad y precio', 'warning');
        return;
      }

      setLoading(true);
      const shipment = getShipmentDetails(newVale.shipment);
      const total = parseFloat(newVale.cantidad) * parseFloat(newVale.precio);
      
      const updatedValeData = {
        ...newVale,
        origen: shipment?.origin || newVale.origen || 'No especificado',
        destino: getDestinationCity(shipment) || newVale.destino || 'No especificado',
        total: total
      };
      
      const idToUse = parseInt(newVale.id, 10);
      if (isNaN(idToUse)) {
        showAlert('Error: El ID del vale no es válido.', 'error');
        return;
      }
      
      const response = await updateValeGasolina(idToUse, updatedValeData);
      setValesGasolina(valesGasolina.map(v => (v.id === idToUse ? response : v)));
      setFilteredVales(valesGasolina.map(v => (v.id === idToUse ? response : v)));
      showAlert('Vale de gasolina actualizado correctamente', 'success');
      
      setNewVale({
        tipo: 'diesel',
        cantidad: '',
        precio: '',
        proveedor: '',
        fecha: new Date().toISOString().split('T')[0]
      });
      setIsEditMode(false);
      setModalOpen(false);
    } catch (error) {
      console.error('Error updating vale de gasolina:', error);
      showAlert(`Error al actualizar vale de gasolina: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    setTimeout(() => {
      navigate('/login');
    }, 0);
  };

  const getShipmentDetails = (shipmentReference) => {
    const shipment = shipments.find(s => s.reference === shipmentReference);
    if (!shipment) return {};
    return shipment;
  };

  const getDestinationCity = (shipment) => {
    if (!shipment || !shipment.orders || shipment.orders.length === 0) return 'N/A';
    const order = shipment.orders[0];
    if (!order.D_R) return order.City || 'N/A';
    const warehouse = warehouseData.find(w => w.nombre === order.customer || w.nombre === order.Customer);
    return warehouse ? warehouse.ciudad_municipio : order.City || 'N/A';
  };

  const calculateTotalVale = (vale) => {
    return parseFloat(vale.total) || 0;
  };

  const handleEditVale = (vale) => {
    if (!vale || vale.id === undefined) {
      alert("Error: El ID del vale no está disponible.");
      return;
    }

    setIsEditMode(true);
    setNewVale({...vale});
    
    setModalOpen(true);
    fetchShipmentForEdit(vale.shipment, vale);
  };

  const fetchShipmentForEdit = async (reference, originalVale) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/shipments?reference=${reference}`);
      setSearchedShipment(response.data[0]);
      setNewVale({...originalVale, fecha: originalVale.fecha ? new Date(originalVale.fecha).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]});
    } catch (error) {
      console.error('Error searching shipment:', error);
      setSearchedShipment(null);
    }
  };

  const handleSearchShipment = async (reference) => {
    try {
      setLoading(true);
      const searchTerm = reference || searchReference;
      if (!searchTerm) {
        showAlert('Ingrese una referencia de embarque para buscar', 'warning');
        return;
      }
      
      const data = await getShipmentByReference(searchTerm);
      
      if (data) {
        if (Array.isArray(data) && data.length > 0) {
          setSearchedShipment(data[0]);
          
          if (!isEditMode) {
            setNewVale(prev => ({
              ...prev,
              shipment: searchTerm,
              tipo: prev.tipo || 'diesel',
              fecha: prev.fecha || new Date().toISOString().split('T')[0]
            }));
          }
        } else {
          setSearchedShipment(data);
          
          if (!isEditMode) {
            setNewVale(prev => ({
              ...prev,
              shipment: searchTerm,
              tipo: prev.tipo || 'diesel',
              fecha: prev.fecha || new Date().toISOString().split('T')[0]
            }));
          }
        }
      } else {
        showAlert('No se encontró ningún embarque con esa referencia', 'warning');
        setSearchedShipment(null);
      }
    } catch (error) {
      console.error('Error searching shipment:', error);
      showAlert(`Error al buscar embarque: ${error.message}`, 'error');
      setSearchedShipment(null);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNewValeModal = () => {
    setIsEditMode(false);
    setNewVale({
      tipo: 'diesel',
      cantidad: '',
      precio: '',
      proveedor: '',
      fecha: new Date().toISOString().split('T')[0]
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setNewVale({
      tipo: 'diesel',
      cantidad: '',
      precio: '',
      proveedor: '',
      fecha: new Date().toISOString().split('T')[0]
    });
    setSearchedShipment(null);
    setSearchReference('');
    setIsEditMode(false);
    setModalOpen(false);
  };

  const applyFilters = () => {
    const filtered = valesGasolina.filter(vale => {
      const shipment = getShipmentDetails(vale.shipment);
      
      const matchesFolio = filters.folio ? 
        vale.folio.toLowerCase().includes(filters.folio.toLowerCase()) : true;
      
      const matchesReference = filters.reference ? 
        vale.shipment?.toLowerCase().includes(filters.reference.toLowerCase()) : true;
      
      const matchesConsignatario = filters.consignatario ?
        shipment.customer?.toLowerCase().includes(filters.consignatario.toLowerCase()) : true;
      
      let matchesDateRange = true;
      if (filters.creationDateRange[0] && filters.creationDateRange[1]) {
        const valeDate = new Date(vale.fecha);
        const startDate = new Date(filters.creationDateRange[0]);
        const endDate = new Date(filters.creationDateRange[1]);
        endDate.setHours(23, 59, 59, 999);
        
        matchesDateRange = valeDate >= startDate && valeDate <= endDate;
      }
      
      return matchesFolio && matchesReference && matchesConsignatario && matchesDateRange;
    });
    
    setFilteredVales(filtered);
  };

  return (
    <div className="flex">
      <Sidebar
        isOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        onLogout={handleLogout}
      />
      <div className={`transition-all flex-1 ${sidebarOpen ? 'ml-64' : 'ml-16'} bg-gray-100 min-h-screen p-6`}>
        <Topbar showSearch={false} />
        
        <div className="mt-6 mb-4">
          <Button 
            className="flex items-center" 
            onClick={handleOpenNewValeModal}
            variant="contained" 
            color="primary"
          >
            <Add className="w-6 h-6 mr-2" />
            Nuevo Vale de Gasolina
          </Button>
        </div>
        
        <div>
          <div className="mb-6 bg-white p-4 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Filtros</h3>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterAlt />}
                variant="outlined"
                size="small"
              >
                {showFilters ? "Ocultar filtros" : "Mostrar filtros"}
              </Button>
            </div>
            
            {showFilters && (
              <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <FormControl sx={{ minWidth: 180 }} size="small">
                  <TextField
                    label="Folio"
                    name="folio"
                    value={filters.folio}
                    onChange={(e) => setFilters({...filters, folio: e.target.value})}
                    size="small"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                      }
                    }}
                  />
                </FormControl>

                <FormControl sx={{ minWidth: 180 }} size="small">
                  <TextField
                    label="Referencia"
                    name="reference"
                    value={filters.reference}
                    onChange={(e) => setFilters({...filters, reference: e.target.value})}
                    size="small"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                      }
                    }}
                  />
                </FormControl>

                <FormControl sx={{ minWidth: 180 }} size="small">
                  <TextField
                    label="Consignatario"
                    name="consignatario"
                    value={filters.consignatario}
                    onChange={(e) => setFilters({...filters, consignatario: e.target.value})}
                    size="small"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                      }
                    }}
                  />
                </FormControl>
                
                <div className="filter-container" style={{ minWidth: 180 }}>
                  <div className="react-datepicker-wrapper">
                    <DatePicker
                      selected={filters.creationDateRange[0]}
                      onChange={(dates) => {
                        const [start, end] = dates;
                        setFilters(prev => ({
                          ...prev,
                          creationDateRange: [start, end]
                        }));
                      }}
                      startDate={filters.creationDateRange[0]}
                      endDate={filters.creationDateRange[1]}
                      selectsRange
                      placeholderText="Rango de fecha de creación"
                      className="p-2 border rounded-md w-full bg-white"
                      dateFormat="dd/MM/yyyy"
                    />
                  </div>
                </div>

                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={applyFilters}
                  sx={{ height: 40 }}
                >
                  Aplicar
                </Button>
                
                <Button 
                  variant="outlined"
                  onClick={() => {
                    setFilters({
                      folio: '',
                      reference: '',
                      consignatario: '',
                      creationDateRange: [null, null]
                    });
                    setFilteredVales(valesGasolina);
                  }}
                  sx={{ height: 40 }}
                >
                  Limpiar
                </Button>
              </Stack>
            )}
          </div>

          <Snackbar 
            open={alertInfo.show} 
            autoHideDuration={6000} 
            onClose={handleCloseAlert}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={handleCloseAlert} severity={alertInfo.severity} sx={{ width: '100%' }}>
              {alertInfo.message}
            </Alert>
          </Snackbar>
          <div className="mb-6">
            <h2 className="text-xl font-bold mb-4">Vales de Gasolina</h2>
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b">Folio</th>
                  <th className="py-2 px-4 border-b">Referencia</th>
                  <th className="py-2 px-4 border-b">Consignatario</th>
                  <th className="py-2 px-4 border-b">Origen</th>
                  <th className="py-2 px-4 border-b">Destino</th>
                  <th className="py-2 px-4 border-b">Conductor</th>
                  <th className="py-2 px-4 border-b">Tipo</th>
                  <th className="py-2 px-4 border-b">Litros</th>
                  <th className="py-2 px-4 border-b">Precio</th>
                  <th className="py-2 px-4 border-b">Fecha</th>
                  <th className="py-2 px-4 border-b">Total</th>
                  <th className="py-2 px-4 border-b">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredVales.map(vale => {
                  const shipment = getShipmentDetails(vale.shipment);
                  return (
                    <tr key={vale.id}>
                      <td className="py-2 px-4 border-b">{vale.folio}</td>
                      <td className="py-2 px-4 border-b">{shipment.reference || 'N/A'}</td>
                      <td className="py-2 px-4 border-b">{shipment.customer || 'N/A'}</td>
                      <td className="py-2 px-4 border-b">{shipment.origin || 'N/A'}</td>
                      <td className="py-2 px-4 border-b">{getDestinationCity(shipment)}</td>
                      <td className="py-2 px-4 border-b">{shipment.driver || 'N/A'}</td>
                      <td className="py-2 px-4 border-b">{vale.tipo || 'N/A'}</td>
                      <td className="py-2 px-4 border-b">{vale.cantidad || 'N/A'}</td>
                      <td className="py-2 px-4 border-b">${vale.precio || 'N/A'}</td>
                      <td className="py-2 px-4 border-b">{vale.fecha || 'N/A'}</td>
                      <td className="py-2 px-4 border-b">${calculateTotalVale(vale)}</td>
                      <td className="py-2 px-4 border-b">
                        <IconButton color="primary" onClick={() => handleEditVale(vale)}>
                          <Edit />
                        </IconButton>
                        <GasolinaPDF vale={{
                          ...vale,
                          driver: shipment?.driver || '',
                          vehicle: shipment?.vehicle || '',
                          origen: shipment?.origin || 'No especificado',
                          destino: getDestinationCity(shipment) || 'No especificado'
                        }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Miuimodal
            open={isModalOpen}
            onClose={handleCloseModal}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 600,
                bgcolor: 'background.paper',
                borderRadius: '10px',
                boxShadow: 24,
                p: 4,
                maxHeight: '90vh',
                overflowY: 'auto'
              }}
            >
              <Typography variant="h6" component="h2">
                {isEditMode ? 'Editar Vale de Gasolina' : 'Crear Nuevo Vale de Gasolina'}
              </Typography>
              {!searchedShipment && !isEditMode && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    label="Referencia del Embarque"
                    value={searchReference}
                    onChange={e => setSearchReference(e.target.value)}
                    fullWidth
                    margin="normal"
                  />
                  <Button
                    startIcon={<Search />}
                    fullWidth
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={() => handleSearchShipment()}
                    disabled={loading}
                  >
                    {loading ? 'Buscando...' : 'Buscar Embarque'}
                  </Button>
                </Box>
              )}
              {searchedShipment && (
                <>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body1"><strong>Referencia:</strong> {searchedShipment.reference}</Typography>
                    <Typography variant="body1"><strong>Consignatario:</strong> {searchedShipment.customer}</Typography>
                    <Typography variant="body1"><strong>Origen:</strong> {searchedShipment.origin}</Typography>
                    <Typography variant="body1"><strong>Destino:</strong> {getDestinationCity(searchedShipment)}</Typography>
                    <Typography variant="body1"><strong>Conductor:</strong> {searchedShipment.driver || 'N/A'}</Typography>
                    <Typography variant="body1"><strong>Vehículo:</strong> {searchedShipment.vehicle || 'N/A'}</Typography>
                  </Box>
                  
                  <TextField
                    label="Folio"
                    value={isEditMode ? newVale.folio : (newVale.folio || generateUniqueFolio())}
                    disabled
                    fullWidth
                    margin="normal"
                  />
                  
                  <TextField
                    label="Fecha"
                    type="date"
                    value={newVale.fecha || ''}
                    onChange={e => setNewVale({ ...newVale, fecha: e.target.value })}
                    fullWidth
                    margin="normal"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  <FormControl fullWidth margin="normal">
                    <InputLabel id="tipo-combustible-label">Tipo de Combustible</InputLabel>
                    <Select
                      labelId="tipo-combustible-label"
                      value={newVale.tipo || 'diesel'}
                      label="Tipo de Combustible"
                      onChange={e => setNewVale({ ...newVale, tipo: e.target.value })}
                    >
                      {fuelTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <TextField
                    label="Cantidad (litros)"
                    type="number"
                    value={newVale.cantidad || ''}
                    onChange={e => setNewVale({ ...newVale, cantidad: e.target.value })}
                    fullWidth
                    margin="normal"
                  />
                  
                  <TextField
                    label="Precio por litro"
                    type="number"
                    value={newVale.precio || ''}
                    onChange={e => setNewVale({ ...newVale, precio: e.target.value })}
                    fullWidth
                    margin="normal"
                  />
                  
                  <TextField
                    label="Proveedor"
                    value={newVale.proveedor || ''}
                    onChange={e => setNewVale({ ...newVale, proveedor: e.target.value })}
                    fullWidth
                    margin="normal"
                    placeholder="Nombre de la estación de gasolina"
                  />
                  
                  <TextField
                    label="Comentarios"
                    value={newVale.comentarios || ''}
                    onChange={e => setNewVale({ ...newVale, comentarios: e.target.value })}
                    fullWidth
                    margin="normal"
                    multiline
                    rows={2}
                    placeholder="Ingrese aquí cualquier comentario relevante"
                  />
                  
                  <Box display="flex" justifyContent="space-between" mt={3}>
                    <Button variant="contained" color="error" onClick={handleCloseModal} disabled={loading}>
                      Cancelar
                    </Button>
                    <Button 
                      variant="contained" 
                      color="success" 
                      onClick={isEditMode ? handleUpdateVale : handleCreateVale}
                      disabled={loading}
                    >
                      {loading ? 'Procesando...' : (isEditMode ? 'Actualizar' : 'Guardar')}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Miuimodal>
        </div>
      </div>
    </div>
  );
};

export default Gasolina;
